<template>
  <app-page-layout root-id="addStudents" page-class="msc-profile-page" :show-notifications="false">
      <div class="row msc-profile-row">
        <div class="col-lg-5 col-md-6 msc-profile-card msc-profile-card--students ml-auto mr-auto">
          <div class="msc-profile-card__header">
            <h2 class="msc-profile-card__title">{{ accountsTitle }}</h2>
            <div class="msc-profile-card__actions">
              <video-help-link
                href="/video/LinkingStudent-Lindy.mp4"
                tip="Video: How to Add/Link a student"
              />
              <button
                class="btn btn-outline-warning btn-round btn-sm msc-profile-btn"
                @click="isAddingStudent = true; newStudent.id = ''; newStudent.dob = ''"
              >
                Add Student
              </button>
            </div>
          </div>

          <div class="msc-profile-card__body">
            <b-tabs card class="msc-student-tabs" content-class="msc-student-tabs__content">
              <h3 class="msc-empty-state" v-if="students.length === 0">Please Add Your Student</h3>
              <template v-for="(student, index) in students">
                <b-tab :title="student.firstName" :key="index">
                  <button
                    type="button"
                    class="msc-action-icon msc-action-icon--danger"
                    @click="removeStudent(student)"
                    v-b-popover.hover="'This action unlinks the selected student from your account and returns them to the available-registrations list.'"
                    aria-label="Unlink student"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                  <template>
                    <s-form-input label="Student ID" disabled type="text" v-model="student.badgeNumber"></s-form-input>
                    <s-form-input label="Grade" disabled type="text" v-model="student.grade"></s-form-input>
                    <s-form-input label="Home Room" disabled type="text" v-model="student.homeRoom"></s-form-input>
                    <s-form-input label="Teacher" disabled type="text" v-model="student.teacher"></s-form-input>
                  </template>
                </b-tab>
              </template>
            </b-tabs>
          </div>
        </div>

        <div class="col-lg-6 col-md-6 msc-profile-card msc-profile-card--payment ml-auto mr-auto">
          <div class="msc-profile-card__header">
            <h2 class="msc-profile-card__title">
              {{ paymentTitle }}
              <video-help-link
                href="/video/paymentDetails.mp4"
                tip="Video: Payment Details Overview"
              />
            </h2>
            <button
              v-if="(!billingInformation.ccInfo.isAuthorized) && (billingInformation.ccInfo.ownerEmail > '')"
              class="btn btn-outline-warning btn-round btn-sm msc-profile-btn"
              @click="registerCreditCard()"
            >
              Add Credit Card
            </button>
          </div>

          <div class="msc-profile-card__body">
            <h3 class="msc-section-heading">
              Your Billing Information
              <small v-if="!isCCAuthorized()" class="msc-required-note">(All Fields Required)</small>
            </h3>
            <p v-if="billingInformation.ccInfo.isAuthorized" class="msc-security-note">
              <strong>NOTE:</strong> For security, once your credit card has been activated on your account, you cannot edit the billing address.
              If you wish to edit the billing information, first remove your credit card, change the billing details, and then re-add the credit card.
            </p>

            <div class="msc-form-panel" @input="isBillingChanged = true">
              <s-form-input label="First Name" :readonly="isCCAuthorized()" type="text" v-model.trim="billingInformation.ccInfo.firstName" placeholder="Match Your Billing Information..."></s-form-input>
              <s-form-input label="Last Name" :readonly="isCCAuthorized()" type="text" v-model.trim="billingInformation.ccInfo.lastName"></s-form-input>
              <s-form-input label="Address" :readonly="isCCAuthorized()" type="text" v-model.trim="billingInformation.ccInfo.address1"></s-form-input>
              <s-form-input label="City" :readonly="isCCAuthorized()" type="text" v-model.trim="billingInformation.ccInfo.city"></s-form-input>
              <s-form-input label="State" :readonly="isCCAuthorized()" field-class="msc-field--state" type="text" v-model.trim="billingInformation.ccInfo.state"></s-form-input>
              <s-form-input label="Zip" :readonly="isCCAuthorized()" field-class="msc-field--short" type="text" v-model.trim="billingInformation.ccInfo.zipCode"></s-form-input>
              <s-form-input label="Phone" :readonly="isCCAuthorized()" field-class="msc-field--short" type="text" v-model.trim="billingInformation.ccInfo.phone"></s-form-input>
              <s-form-input label="Email" type="email" v-model.trim="billingInformation.ccInfo.ownerEmail" placeholder="For sending receipts..."></s-form-input>
              <s-form-input
                label="Billing Reference"
                type="text"
                v-model.trim="billingInformation.reference"
                placeholder="Your Memo, NOT CC#; e.g., School Lunch"
                :help="billingRefHelpText"
                @help-click="showHelp(billingRefHelpText)"
              ></s-form-input>

              <b-form-group v-if="portal" horizontal label-cols-lg="3" label-class="text-sm-right msc-form-label" label="End Of Year Balance" class="msc-form-group msc-form-group--with-help">
                <template #label>
                  <span class="msc-form-label__wrap">
                    <span class="msc-form-label__text">End Of Year Balance</span>
                    <help-tip :tip="eoyFunds" />
                  </span>
                </template>
                <b-form-select class="form-control msc-form-control msc-field--medium" v-model="portal.endOfYear.remainingBalanceAction" :value="portal.endOfYear.remainingBalanceAction" :options="eoyBalanceOptions">
                </b-form-select>
              </b-form-group>

              <notifications></notifications>

              <div class="msc-form-actions" v-show="isBillingChanged">
                <b-button class="btn btn-warning btn-round btn-sm msc-profile-btn" @click="saveBillingInformation()">
                  Save Changes
                </b-button>
              </div>

              <hr class="msc-divider">

              <template v-if="billingInformation.ccInfo.isAuthorized">
                <div class="msc-cc-panel">
                  <loading :active.sync="isShowLoading" :is-full-page="true" :height="200" :width="200" color="coral"></loading>
                  <div class="msc-cc-panel__header">
                    <h4 class="msc-cc-panel__title">Card on File</h4>
                    <button
                      type="button"
                      class="msc-action-icon msc-action-icon--danger"
                      @click="removeCreditCard()"
                      v-b-popover.hover="'Remove this credit card from your account. Once removed, you may add another (or the same if only the billing information changed)'"
                      aria-label="Remove credit card"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                  <s-form-input label="Card Type" field-class="msc-field--state" readonly type="text" v-model="billingInformation.ccInfo.ccType"></s-form-input>
                  <s-form-input label="Card Number" field-class="msc-field--card" readonly type="text" v-model="billingInformation.ccInfo.ccNumber"></s-form-input>
                  <s-form-input label="Expiration Date" field-class="msc-field--short" readonly type="text" v-model="billingInformation.ccInfo.ccExpDate"></s-form-input>
                </div>
              </template>

              <template v-if="!billingInformation.ccInfo.isAuthorized && !isAddingCreditCard && !isBillingChanged && billingInformation.ccInfo.ownerEmail.indexOf('@') > 0">
                <div class="msc-inline-action">
                  <button class="btn btn-outline-warning btn-round btn-sm msc-profile-btn" @click="registerCreditCard()">
                    Add Credit Card
                  </button>
                </div>
              </template>

              <b-form-group v-if="billingInformation.ccInfo.isAuthorized" horizontal label-cols-lg="3" label-class="text-sm-right msc-form-label" label="Reload Value" class="msc-form-group">
                <div class="msc-reload-row">
                  <b-form-select class="form-control msc-form-control msc-field--short" v-model="billingInformation.autoReloadTotal" :value="billingInformation.autoReloadTotal" :options="reloadValues">
                  </b-form-select>
                  <button v-if="!isAddingCreditCard && !isBillingChanged" class="btn btn-success btn-round btn-sm msc-profile-btn" @click="reloadStudentBalance()">
                    Load This Amount Now
                  </button>
                  <b-button class="btn btn-warning btn-round btn-sm msc-profile-btn" v-show="isBillingChanged" @click="saveBillingInformation()">
                    Save Changes
                  </b-button>
                </div>
                <div v-if="reloadAccountError.length > 0" @click="reloadAccountError = ''">
                  <b-alert variant="danger" show>{{ reloadAccountError }}</b-alert>
                </div>
              </b-form-group>
            </div>

            <template v-if="isAddingCreditCard">
              <loading :active.sync="isShowLoading" :is-full-page="true" :height="200" :width="200" color="coral"></loading>
              <img class="msc-cc-logos" src="/img/creditcard-logos.png" alt="Accepted credit cards" />
              <h3 class="msc-section-heading msc-section-heading--sub">Register Your Credit Card</h3>
              <div v-show="addCreditCardError.length > 0">
                <b-alert dismissible variant="danger" show>{{ addCreditCardError }}</b-alert>
              </div>
              <s-form-input iRef="ccnumber" id="cc-number" label="Card Number" field-class="msc-field--card" type="text" v-model="newCC.card" placeholder="Your Credit Card"></s-form-input>
              <s-form-input id="cc-expire" label="Expiration Date" field-class="msc-field--short" type="card-exp" v-model="newCC.expDate"></s-form-input>
              <b-alert v-if="cardExpInvalidMonth" class="msc-cc-exp-alert" variant="danger" show>
                Please enter a valid expiration month (01–12).
              </b-alert>
              <b-alert v-else-if="cardExpExpired" class="msc-cc-exp-alert" variant="danger" show>
                This card appears to be expired. Please enter a current expiration date.
              </b-alert>
              <b-alert v-else-if="cardExpiringSoon" class="msc-cc-exp-alert msc-cc-exp-alert--warning" variant="warning" show>
                {{ cardExpiringSoonMessage }}
              </b-alert>
              <s-form-input id="cc-cvv" label="CVV" field-class="msc-field--short" type="text" v-model="newCC.cvv"></s-form-input>
              <div class="msc-cc-actions">
                <button
                  v-show="!isShowLoading"
                  class="btn btn-success btn-round btn-sm msc-profile-btn"
                  :disabled="!canAuthorizeCard"
                  @click="registerCreditCard()"
                >
                  I'm Ready, Let's Authorize This Card
                </button>
                <button v-show="!isShowLoading" class="btn btn-danger btn-round btn-sm msc-profile-btn" @click="isAddingCreditCard = false">
                  Nope, Not Right Now
                </button>
              </div>
            </template>
          </div>

          <div class="msc-profile-card__footer">
            <button class="btn btn-secondary btn-round btn-sm msc-profile-btn" @click="transactionSummary()">
              Payments Summary
            </button>
            <h3 class="msc-balance">Balance: ${{ currency(accountBalance) }}</h3>
          </div>
        </div>
      </div>

      <div class="row msc-payment-summary-row" id="ccSummary">
        <div v-if="showCCSummary" class="col-md-8 ml-auto mr-auto msc-payment-summary">
          <h2 class="title">Payment Listing</h2>
          <el-table :default-sort="{ prop: 'transactionDate', order: 'descending' }" :data="paymentList">
            <el-table-column width="60" min-width="60" type="index" sortable></el-table-column>
            <el-table-column min-width="150" prop="transactionDate" label="Date" sortable>
              <template slot-scope="scope">
                {{ moment(scope.row.transactionDate).format("DD-MMM-YY hh:mma") }}
              </template>
            </el-table-column>
            <el-table-column min-width="150" prop="title" label="Action" sortable></el-table-column>
            <el-table-column min-width="100" align="right" prop="tenderAmount" label="Total" sortable>
              <template slot-scope="scope">
                {{ currency(scope.row.tenderAmount) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

    <modal
      :show.sync="isAddingStudent"
      modal-classes="msc-app-modal"
      header-classes="msc-app-modal__header"
      body-classes="msc-app-modal__body"
      footer-classes="msc-app-modal__footer"
    >
      <h4 slot="header" class="msc-modal-title">Link Your Student</h4>
      <p class="msc-modal-intro">
        Linking a student is required to connect a registered student with this account.
        The student ID is provided on the school registration sheet.
      </p>
      <s-form-input label="Student ID" type="text" v-model="newStudent.id"></s-form-input>
      <s-form-input label="Student DOB" type="dob" v-model="newStudent.dob"></s-form-input>
      <p v-if="addStudentError" class="msc-modal-error">{{ addStudentError }}</p>
      <p v-if="isAddingMessage" class="msc-modal-intro">
        {{ isAddingMessage }}
      </p>
      <template slot="footer">
        <div class="msc-modal-footer-actions">
          <button type="button" class="btn btn-secondary btn-round btn-sm msc-profile-btn" @click="isAddingStudent = false">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-success btn-round btn-sm msc-profile-btn"
            :disabled="!canAddStudent"
            @click="addStudent()"
          >
            Add This Student
          </button>
        </div>
      </template>
    </modal>
  </app-page-layout>
</template>
<script>
  import AppPageLayout from '@/layout/AppPageLayout.vue';

  import { Badge, Button, Card, Checkbox, FormGroupInput, HelpTip, InfoSection, Modal, SFormInput, TabPane, Tabs, VideoHelpLink } from '@/components';
  import GeneralService                                                                                   from "@/services/GeneralService";
  import Shift4                                                                                           from "@/services/Shift4";
  import Currency                                                                                         from "currency.js";
  import { Table, TableColumn }                                                                           from 'element-ui';
  import Moment                                                                                           from "moment";
  import Loading                                                                                          from "vue-loading-overlay";
  import Datepicker from 'vuejs-datepicker';
  import "vue-loading-overlay/dist/vue-loading.css";
  import {
    cardExpToApiFormat,
    dobToApiFormat,
    formatCardExpDisplay,
    isCardExpComplete,
    isCardExpExpired,
    isCardExpiringSoon,
    isCardExpValid,
    isDobComplete,
    parseCardExp,
  } from '@/utils/dateFormat';

  export default {
    name      : 'profile-page',
    components: {
      AppPageLayout,
      SFormInput,
      HelpTip,
      VideoHelpLink,
      InfoSection,
      Card,
      Tabs,
      TabPane,
      Modal,
      Badge,
      Datepicker,
      Loading,
      [ Button.name ]        : Button,
      [ Checkbox.name ]      : Checkbox,
      [ FormGroupInput.name ]: FormGroupInput,
      [ Table.name ]         : Table,
      [ TableColumn.name ]   : TableColumn
    },
    data() {
      return {
        isShowLoading   : false,
        isBillingChanged: false,
        isStudentChanged: false,
        isAddingMessage : "",
        portal          : null,
        accountsTitle   : "Students",
        paymentTitle    : "Payment Details",
        accountBalance  : 0.00,
        availableSpend  : 0.00,
        reloadValues    : [
          {value: 0, text: "Please Select"},
          {value: 25, text: "$25.00"},
          {value: 50, text: "$50.00"},
          {value: 75, text: "$75.00"},
          {value: 100, text: "$100.00"},
          {value: 125, text: "$125.00"},
          {value: 150, text: "$150.00"},
          {value: 170, text: "$175.00"},
          {value: 200, text: "$200.00"}
        ],
        billingRefHelpText: "This is your REFERENCE for tracking on your statement and email notifications.  e.g., Suzie Lunch or School Lunch.  Please do NOT enter your credit card number here.",
        eoyFunds: "This determines your preference for how the remaining balance in your account is managed at the end of the school year.",
        showCCSummary     : false,
        isAddingCreditCard: false,
        addCreditCardError: "",
        isAddingStudent   : false,
        addStudentError   : "",
        reloadAccountError: "",
        paymentList       : [],
        newCC             : {
          card   : "",
          expDate: "",
          cvv    : ""
        },
        balance           : {
          "accountNumber": "",
          "totalClassId" : 0,
          "totalType"    : "debit",
          "balance"      : 0,
          "total"        : {
            "chargeTotal" : 0,
            "chargeQty"   : 0,
            "paymentTotal": 0,
            "paymentQty"  : 0
          }
        },
        billingInformation: {
          "options"      : {
            "isAutoReload"  : false
          },
          "ccInfo"       : {
            "ccNumber"    : "",
            "ccType"      : "",
            "ccExpDate"   : "",
            "isAuthorized": false,
            "isDebit"     : false,
            "firstName"   : "",
            "lastName"    : "",
            "address1"    : "",
            "city"        : "",
            "state"       : "",
            "zipCode"     : "",
            "phone"       : "",
            "ownerEmail"  : ""
          },
          title          : "",
          reference      : "",
          autoReloadTotal: 0,
          transactionList: []
        },
        eoyBalanceOptions: [
          { text: 'Donate Unused to School', value: 'donate' },
          { text: 'Rollover Unused Until Next Year', value: 'rollover' },
          { text: 'Refund Unused to My Card', value: 'refund' }
        ],
        newStudent    : {
          id: "",
          dob: ""
        },
        students      : [],

        form          : {
          firstName : '',
          email     : '',
          speciality: ''
        },
        studentRecords: [ 'one' ]
      }
    },
    computed  : {
      canAddStudent() {
        const studentId = String(this.newStudent.id || '').trim();
        return studentId.length > 0 && isDobComplete(this.newStudent.dob);
      },
      canAuthorizeCard() {
        const card = String(this.newCC.card || '').replace(/\D/g, '');
        const cvv = String(this.newCC.cvv || '').trim();
        const cc = this.billingInformation.ccInfo;

        const billingComplete = [
          cc.firstName,
          cc.lastName,
          cc.address1,
          cc.city,
          cc.state,
          cc.zipCode,
          cc.phone,
          cc.ownerEmail,
        ].every((value) => String(value || '').trim().length > 0)
          && String(cc.ownerEmail || '').indexOf('@') > 0;

        return billingComplete
          && card.length >= 15
          && card.length <= 16
          && isCardExpValid(this.newCC.expDate)
          && cvv.length >= 3
          && cvv.length <= 5;
      },
      cardExpInvalidMonth() {
        return isCardExpComplete(this.newCC.expDate) && parseCardExp(this.newCC.expDate) === null;
      },
      cardExpExpired() {
        return isCardExpComplete(this.newCC.expDate)
          && parseCardExp(this.newCC.expDate) !== null
          && isCardExpExpired(this.newCC.expDate);
      },
      cardExpiringSoon() {
        return isCardExpiringSoon(this.newCC.expDate, 6);
      },
      cardExpiringSoonMessage() {
        const display = formatCardExpDisplay(this.newCC.expDate);
        const when = display ? ` on ${display}` : ' soon';
        return `Friendly heads-up: this card expires${when}. That's not a problem — you can still register it today. We just wanted to let you know in case you'd prefer a card that stays active longer for school-year payments.`;
      },
      summaryTotal: function() {
        let total = 0;
        for(let i = 0; i < this.paymentList.length; i++){
          total += parseFloat(this.paymentList[ i ].tenderAmount);
        }
        return total;
      }
    },
    async mounted() {
      await Promise.all([
                          this.getBillingInformation(),
                          this.getStudents(),
                          this.getBalance()
                        ])

    },
    async beforeRouteLeave( to , from, next ) {
      if( !this.isBillingChanged && ! this.isStudentChanged && !this.isAddingCreditCard) {
        return next();
      }
      if( await this.$root.confirmYesNo('You have unsaved changes, continue leaving without saving?', "warning")) {
        return next();
      }
      next(false)
    },
    methods   : {
      currency(c) {
        return Currency(c);
      },
      moment(d) {
        return Moment(d);
      },
      async showHelp(helpText) {
        await this.$root.alert(helpText);
      },
      isCCAuthorized() {
        return this.billingInformation.ccInfo.isAuthorized
      },
      makeReloadValueSelectionList() {
        let t          = this;
        let minValue   = Math.min((25 * (t.students.length - 1)) + 25, 75);
        t.reloadValues = [];

        if(t.billingInformation.autoReloadTotal < minValue) {
          t.isBillingChanged = true;
          t.billingInformation.autoReloadTotal = minValue;
        }

        for(var i = minValue; i <= 200; i += 25){
          t.reloadValues.push({value: i, text: "$" + t.currency(i).toString()});
        }
      },
      async getStudents() {

        let t = this;

        try {
          let response  = await GeneralService.getStudents();
          this.students = response.data.students;

        } catch(error) {
          console.error(error);
        }
        this.makeReloadValueSelectionList();
      },
      async addStudent() {
        let t = this;

        if (!this.canAddStudent) {
          return;
        }

        let regInfo = {
          table   : "student",
          idNumber: t.newStudent.id,
          dob     : dobToApiFormat(t.newStudent.dob)
        }

        if(regInfo.idNumber === "") {
          t.addStudentError = "Please Enter a Student Id";
          return;
        }

        if(regInfo.dob === "") {
          t.addStudentError = "Please Enter a DOB";
          return;
        }

        if( parseInt(regInfo.dob.substring( 0 ,2 )) > 12 ) {
          t.addStudentError= "The Date Format is MM/DD/YYYY";
          return;
        }

        if(regInfo.dob.indexOf( "/") < 0 && regInfo.dob.indexOf( "-") <0 ) {
          if( regInfo.dob.length === 8 ) {
            let dob = regInfo.dob.replace(/(\d{2})(\d{2})(\d{4})/, "$1-$2-$3");
            regInfo.dob = dob;
            t.newStudent.dob = dob.replace(/-/g, '/');
          } else {
            t.addStudentError =  "Invalid Date Format - Please Add Slashes"
            return;
          }
        }

        if( regInfo.dob.length !== 10 ) {
          t.addStudentError = "The Date Format is MM/DD/YYYY (leading zeros also please)";
          return;
        }

        t.addStudentError = "";
        t.reloadAccountError = "";

        try {
          let result = await GeneralService.linkStudent(regInfo);
          this.getStudents();
          this.makeReloadValueSelectionList();
          this.isAddingStudent = false;
        } catch(error) {
          console.error(error);

          if(error.response) {
            if(error.response.data.message) {
              let message = error.response.data.message;
              if(error.response.data.reason) {
                message += "  " + error.response.data.reason
              }
              t.addStudentError = message;
            }
          } else {
            t.addStudentError = "OOPS - Had an issue adding this student";
          }

          setTimeout(function() {
            t.addStudentError = "";
          }, 5000);
        }
      },
      async removeStudent(student) {
        if(student.studentId > "") {
          if(!await this.$root.confirmYesNo("Un-select this student from your account: " + student.firstNname + "?")) {
            return;
          }

          student.table = "student";
          try {
            let result = await GeneralService.unlinkStudent( student );

            this.getStudents();
            this.makeReloadValueSelectionList();

            this.notify("success", "Student record has been unassigned from your account" );

          } catch( error ) {
            this.notify("error", error.message );
            console.error( error );
          }

        }
      },
      async saveStudents() {
        await this.$root.alert("Save Students - Not Implemented Yet");
      },
      async reloadStudentBalance() {
        let reloadTotal = parseFloat(this.billingInformation.autoReloadTotal);

        if(!await this.$root.confirmYesNo("Add $" + this.currency(reloadTotal).toString() + " To Your Account?")) {
          return;
        }

        if(!await this.$root.confirmYesNo("This action will cause your credit card to be charged $" + this.currency(reloadTotal).toString() + "   Continue?")) {
          return;
        }

        let response;
        this.reloadAccountError = "";
        this.addCreditCardError = "";

        try {
          this.isShowLoading = true;
          response           = await GeneralService.manualReload();

          this.accountBalance = response.data.total.balance;

          this.showCCSummary = false;
          this.transactionSummary();

          this.reloadAccountError = "";
          this.notify("info", this.currency(reloadTotal).toString() + " Has Been Added");

        } catch(error) {

          if(error.response) {
            if(error.response.data.message) {
              let message = error.response.data.message;
              if(error.response.data.reason) {
                message += "  " + error.response.data.reason
              }
              this.setReloadAccountError(message)
            }
          } else {
            this.setReloadAccountError("oops, had an issue");
          }

        }

        this.isShowLoading = false;
      },
      async setReloadAccountError(message) {
        let t = this;
        t.notify("danger", message);
        t.reloadAccountError = message;
      },
      setAddCCError(message) {
        let t = this;
        t.notify("danger", message);
        t.addCreditCardError = "[x]   " + message;
        setTimeout(function() {
          t.addCreditCardError = "";
        }, 5000)
      },
      async saveBillingInformation() {
        let t                = this;
        try {
          let response         = await GeneralService.saveBillingInformation({billing: t.billingInformation, portal: t.portal });
          t.billingInformation = response.data.billing;
          t.isBillingChanged = false;
          t.notify("success", "Your information has been saved.");
        } catch( error ) {
          console.log( error );
          if(error.response) {
            if(error.response.data.message) {
              this.setAddCCError(error.response.data.message)
            }
          } else {
            this.setAddCCError(error.message);
          }
        }
        t.makeReloadValueSelectionList();
      },
      async saveOptions() {
        try {
            await GeneralService.saveOptions( portal.endOfYear );
        } catch( error ) {
          console.log( error );
        }
      },
      async getBillingInformation() {
        let t                = this;
        try {
          let response             = await GeneralService.getBillingInformation();
              t.billingInformation = response.data.billing;
              if (t.billingInformation.ccInfo.ccExpDate) {
                t.billingInformation.ccInfo.ccExpDate = formatCardExpDisplay(t.billingInformation.ccInfo.ccExpDate);
              }
              t.portal             = response.data.portal;
              t.accountNumnber     = response.data.accountNumber;
              t.accountTitle       = response.data.accountTitle;
              t.isBillingChanged   = false;
        } catch( error ) {
          console.log( error );
        }
        t.makeReloadValueSelectionList();
      },
      async getPaymentList() {
        let t         = this;
        try {
          let response  = await GeneralService.getPaymentList();
          t.paymentList = response.data.transactions;
        } catch (error ) {
          console.log( error );
          if(error.response) {
            if(error.response.data.message) {
              this.setAddCCError(error.response.data.message)
            }
          } else {
            this.setAddCCError(error.message);
          }
        }
      },
      async registerCreditCard() {
        if(this.billingInformation.ccInfo.isAuthorized && !this.isAddingCreditCard) {
          await this.$root.alert("Only One Credit Card Allowed");
          return;
        }

        this.reloadAccountError = "";
        this.addCreditCardError = "";

        if(this.isAddingCreditCard) {

          if (!this.canAuthorizeCard) {
            return;
          }

          this.newCC.expDate = cardExpToApiFormat(this.newCC.expDate);

          if(this.newCC.card.length < 15 || this.newCC.card.length > 16) { return this.setAddCCError("Invalid Card Number"); }
          if(this.newCC.cvv.length < 3 || this.newCC.cvv.length > 5) { return this.setAddCCError("Incorrect C V V length (see back of card)"); }
          if(this.newCC.expDate.length !== 4) { return this.setAddCCError("Check Expiration Date - Should Be MM/YY"); }
          if(isCardExpExpired(this.newCC.expDate)) {
            return this.setAddCCError("This card appears to be expired. Please enter a current expiration date.");
          }

          if(this.billingInformation.ccInfo.firstName === 0) { return this.setAddCCError("First Name Required"); }
          if(this.billingInformation.ccInfo.lastName === 0) { return this.setAddCCError("Last Name Required"); }
          if(this.billingInformation.ccInfo.address1 === 0) { return this.setAddCCError("Address Required"); }
          if(this.billingInformation.ccInfo.city === 0) { return this.setAddCCError("City Required"); }
          if(this.billingInformation.ccInfo.state === 0) { return this.setAddCCError("State Required"); }
          if(this.billingInformation.ccInfo.zipCode === 0) { return this.setAddCCError("Zipcode Required"); }
          if(this.billingInformation.ccInfo.phone === 0) { return this.setAddCCError("Phone Number Required"); }
          if(this.billingInformation.ccInfo.ownerEmail === 0) { return this.setAddCCError("Billing Email Address Required"); }

          let response;

          try {
            this.isShowLoading = true;

            response  = await GeneralService.getAccessToken();
            let token = response.data;

            if(token.i4go_response !== "SUCCESS") {
              this.setAddCCError("Oops, Unable to Retrieve AccessToken");
              this.isShowLoading = false;
              return;
            }

            token.ccInfo = this.billingInformation.ccInfo;
            token.newCC  = this.newCC;
            response = await Shift4.tokenizeCard(token);
            response = await GeneralService.registerCC(response.data);

            this.billingInformation = response.data.billing;
            if (this.billingInformation.ccInfo.ccExpDate) {
              this.billingInformation.ccInfo.ccExpDate = formatCardExpDisplay(this.billingInformation.ccInfo.ccExpDate);
            }
            this.accountNumnber     = response.data.accountNumber;
            this.accountTitle       = response.data.accountTitle;
            this.isShowLoading      = false;
            this.isBillingChanged   = false;
            this.isAddingCreditCard = false;
            this.newCC.card         = "";
            this.notify("success", "Card Registered Successfully");

            return;
          } catch(error) {
            this.isShowLoading = false;
            console.log( "register CC Error: ", error);
            if(error.response) {
              if(error.response.data.message) {
                this.setAddCCError(error.response.data.message)
                await this.$root.confirmYesNo( error.response.data.message , 'warning');
              }
            } else {
              this.setAddCCError(error.message);
              await this.$root.confirmYesNo( error.message , 'warning');
            }
          }
          this.isShowLoading = false;
        } else {
          let t=this;
          t.isAddingCreditCard = true;
          t.newCC.card         = "";
          t.newCC.cvv          = "";
          t.newCC.expDate      = "";
          this.$nextTick(() => {
            let e = document.getElementById( "cc-number" );
            if( e ) {
              e.focus();
            }
          });
        }
      },
      async removeCreditCard() {
        if(this.billingInformation.ccInfo.ccNumber === "") {
          return;
        }

        if(!await this.$root.confirmYesNo("Remove/Unregister This Credit Card? " + this.billingInformation.ccInfo.ccNumber)) {
          return;
        }

        let response;
        try {
          this.isShowLoading      = true;
          response                = await GeneralService.removeCC();
          this.billingInformation = response.data.billing;
          if (this.billingInformation.ccInfo.ccExpDate) {
            this.billingInformation.ccInfo.ccExpDate = formatCardExpDisplay(this.billingInformation.ccInfo.ccExpDate);
          }
          this.isBillingChanged   = false;
          this.isAddingCreditCard = false;
          this.notify("success", "Card Removed Successfully");
        } catch(error) {
          console.log(error);
          if(error.response) {
            if(error.response.data.message) {
              this.setAddCCError(error.response.data.message)
            }
          } else {
            this.setAddCCError("oops, had an issue removing card");
          }
        }
        this.isShowLoading = false;
      },
      async transactionSummary() {
        this.showCCSummary = !this.showCCSummary;

        if(this.showCCSummary) {
          await this.getPaymentList();
          this.$router.push( "#ccSummary" );
        }
      },
      async getBalance() {
        let response;

        try {
          response            = await GeneralService.getBalance();
          this.accountBalance = response.data.balance;
          this.availableSpend = response.data.availableSpend;
        } catch(error) {
          console.error(error);
          this.notify("danger", error.message);
        }
      },
      notify(type, message) {
        let t = this;
        let icon;

        if(type === "danger") {
          icon = "fa fa-error";
        } else if(type === "success") {
          icon = "fa fa-ok";
        } else if(type === "error") {
          type = "danger";
          icon = "fa fa-danger";
        } else {
          icon = "fa fa-warning";
        }

        t.$notify({
                    message        : message,
                    icon           : icon,
                    horizontalAlign: 'center',
                    verticalAlign  : 'bottom',
                    type           : type
                  })
      }

    }
  }
</script>
